import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { Truck, Clock, ArrowRight, MapPin } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Delivery Areas", item: `${SITE}/delivery` },
  ],
};

const areas = [
  { title: "Walt Disney World Resorts", link: "/delivery/walt-disney-world-resorts", desc: "All Disney resort hotels. In-person lobby delivery and pickup.", icon: "✦" },
  { title: "Universal Orlando", link: "/delivery/universal-orlando", desc: "Universal resort hotels and on-site properties.", icon: "✦" },
  { title: "Vacation Homes & Rentals", link: "/delivery/vacation-homes", desc: "Airbnb, VRBO and private vacation homes across the Orlando area.", icon: "✦" },
  { title: "Disney Springs Area Hotels", link: "/delivery", desc: "Hotels within the Disney Springs resort area.", icon: "✦" },
  { title: "Bonnet Creek & Reunion", link: "/delivery", desc: "Vacation clubs and resort communities near Walt Disney World.", icon: "✦" },
  { title: "Kissimmee & Celebration", link: "/delivery", desc: "Hotels, homes and rentals throughout the Kissimmee and Celebration areas.", icon: "✦" },
];

export default function DeliveryHub() {
  return (
    <>
      <PageSEO
        title="Stroller Delivery Areas | Walt Disney World, Universal & Orlando"
        description="Enchanted Gear Rentals delivers premium strollers and baby gear free to Walt Disney World resorts, Universal Orlando, vacation homes and area hotels throughout Orlando."
        canonical={`${SITE}/delivery`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Delivery Areas</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Stroller Delivery Areas</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              We deliver premium strollers and baby gear free throughout the greater Orlando area — resort hotels, vacation homes, Airbnb properties and more.
            </p>
          </div>
        </section>

        {/* Delivery basics */}
        <section className="container py-12">
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { Icon: Truck, title: "Free delivery", body: "Standard delivery is always free. No early-delivery surcharges." },
              { Icon: Clock, title: "Delivery windows", body: "One-hour delivery windows available 7 am – 10 pm. Pick the exact hour that works for your schedule." },
              { Icon: MapPin, title: "In-person service", body: "We meet you at your resort, hotel or home. We never leave equipment unattended." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-5">
                <Icon className="w-5 h-5 text-[#FFD166] mb-3" />
                <h2 className="font-serif text-lg text-[#F6F1E4] mb-2">{title}</h2>
                <p className="text-sm text-[#B9C8D5] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Area links */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Where we deliver</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {areas.map((a) => (
              <Link key={a.title} to={a.link} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all hover:-translate-y-1">
                <div className="text-[#FFD166] text-xl mb-2">{a.icon}</div>
                <h3 className="font-serif text-lg text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-1">{a.title}</h3>
                <p className="text-sm text-[#B9C8D5]">{a.desc}</p>
                <div className="mt-3 text-xs text-[#FFD166] flex items-center gap-1">Delivery details <ArrowRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
        </section>

        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Ready to reserve?</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and delivery address to check stroller availability and confirm your booking.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="delivery-hub-cta">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
